<template>
  <div class="company-name small-12 columns">
    <label>{{ $t('settings.user.vendor.userType') }}</label>
    <HKLMultiSelect
      class="small-5 columns"
      :options="vendorTypeOptions"
      :value="vendorParams.vendorType"
      @change="val => updateUserType(val)"
    />
    <label>{{ $t('tenancy.companyId') }}</label>
    <div>
      <HKLInput
        v-model="vendorParams.companyId"
        :class="'small-9 columns'"
        type="text"
      />
    </div>
    <label>{{ $t('eCircular.companyName') }}</label>
    <div class="company-name-search-attribute">
      <HKLInput
        v-model="vendorParams.companyName"
        :class="'small-9 columns'"
        type="text"
      />
      <HKLButton
        color="darkblue"
        action="search"
        :disabled="isVendorTypeEmpty"
        @click="_searchRecipients(false)"
      />
    </div>
    <ActionConfirmModal
      ref="filterRemoveConfirmModal"
      :modalId="filterRemoveConfirmModalId"
      :confirm-title="'eCircular.confirmRemoveUserTypeBody'"
      modal-type="small-modal"
      class="ecircular-modal"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { cloneDeep, get, isEmpty, isEqual } from 'lodash'
import { computed } from 'vue-demi'
import mutationTypes from '@/store/mutation-types'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect'
import HKLInput from '@/controls/Input/HKLInput'
import HKLButton from '@/controls/Button/HKLButton'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import i18n from '@/utils/i18n'
import {
  pruneAddedRecipientsExclusiveVendorType,
  pruneAddedRecipientsAnyLeafWithVendorType,
} from './helper'

export default {
  name: 'VendorRecipientsSearch',
  components: {
    HKLButton,
    HKLMultiSelect,
    HKLInput,
    ActionConfirmModal,
  },
  props: {
    initSelectRecipientsTree: {
      type: Function,
      default: () => {},
    },
  },
  setup () {
    return {
      vendorTypeOptions: computed(() => [{
        key: 'admin',
        value: 'admin',
        name: i18n.t('eCircular.recipientType.admin'),
      }, {
        key: 'sub',
        value: 'subUser',
        name: i18n.t('eCircular.recipientType.sub'),
      }, {
        key: 'contact',
        value: 'contact',
        name: i18n.t('eCircular.recipientType.contact'),
      }]),
    }
  },
  data () {
    return {
      filterRemoveConfirmModalId: `filterRemoveVendorRecipients-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      vendorParams: {
        companyName: '',
        companyId: '',
        vendorType: [],
      },
    }
  },
  computed: {
    ...mapGetters('ecircular', ['currentECircular', 'addedRecipients', 'searchRecipientsResult', 'searchVendorRecipientsConditions']),
    isVendorTypeEmpty () {
      return !Array.isArray(this.vendorParams.vendorType) || this.vendorParams.vendorType.length === 0
    },
  },
  async mounted () {
    if (!isEmpty(this.searchVendorRecipientsConditions)) {
      this.vendorParams = cloneDeep(this.searchVendorRecipientsConditions)
    } else {
      this.restoreVendorTypeFromSavedUserType()
    }
  },
  methods: {
    ...mapActions('ecircular', ['searchRecipients']),
    ...mapMutations('ecircular', {
      saveConditions: mutationTypes.ECIRCULAR_SAVE_SEARCH_VENDOR_RECIPIENT_CONDITIONS,
      updateAddedRecipients: mutationTypes.ECIRULAR_ADDED_RECIPIENTS,
    },
    ),
    restoreVendorTypeFromSavedUserType () {
      const saved = get(this.currentECircular, 'userType', [])
      if (!Array.isArray(saved) || !saved.length) return
      const allowed = new Set(['admin', 'subUser', 'contact'])
      this.vendorParams.vendorType = [...new Set(
        saved
          .map((t) => this.normalizeVendorTypeToken(String(t)))
          .filter((t) => allowed.has(t)),
      )]
    },
    normalizeVendorTypeToken (token) {
      return token === 'sub' ? 'subUser' : token
    },
    normalizeVendorTypeList (types = []) {
      return [...new Set((types || []).map((t) => this.normalizeVendorTypeToken(String(t))))].filter(Boolean)
    },
    getRemovedVendorTypes (before = [], after = []) {
      const beforeNorm = this.normalizeVendorTypeList(before)
      const afterNorm = this.normalizeVendorTypeList(after)
      const afterSet = new Set(afterNorm)
      return beforeNorm.filter((t) => !afterSet.has(t))
    },
    getPrunedRecipientsByVendorSelection ({ selectedAfterRemove = [], removedTypes = [], recipientTrees = this.addedRecipients }) {
      const activeAfter = new Set(this.normalizeVendorTypeList(selectedAfterRemove))
      return (removedTypes || []).reduce((nextRecipients, removedType) => {
        const hasOtherActiveTypes = [...activeAfter].some((type) => type !== removedType)
        return hasOtherActiveTypes
          ? pruneAddedRecipientsExclusiveVendorType(nextRecipients, removedType)
          : pruneAddedRecipientsAnyLeafWithVendorType(nextRecipients, removedType)
      }, cloneDeep(recipientTrees || []))
    },
    promptRemoveFilterRecipients (onConfirm, onCancel) {
      this.$refs.filterRemoveConfirmModal.open(onConfirm, [], onCancel)
    },
    refreshRecipientTreesAfterPrune (remainingSelectableTrees) {
      this.initSelectRecipientsTree('select-recipients', remainingSelectableTrees)
      this.initSelectRecipientsTree('delete-recipients')
    },
    applyPrunedAddedRecipients ({ addedRecipients, remainingSelectableTrees }) {
      this.updateAddedRecipients(addedRecipients)
      this.$nextTick(() => this.refreshRecipientTreesAfterPrune(remainingSelectableTrees))
    },
    confirmAndApplyPruneIfNeeded ({ nextAfterPrune, remainingSelectableTrees, onCancel }) {
      if (isEqual(nextAfterPrune, this.addedRecipients)) {
        this.$nextTick(() => this.refreshRecipientTreesAfterPrune(remainingSelectableTrees))
        return
      }
      this.promptRemoveFilterRecipients(
        () => {
          this.applyPrunedAddedRecipients({
            addedRecipients: nextAfterPrune,
            remainingSelectableTrees,
          })
        },
        onCancel,
      )
    },
    syncVendorSearchConditionsToStore () {
      this.saveConditions({
        ...cloneDeep(this.vendorParams),
        ecircularId: this.currentECircular.id,
      })
    },
    updateUserType (val) {
      const snapshot = cloneDeep(this.vendorParams.vendorType || [])
      const next = Array.isArray(val) ? val : []
      const removedTypes = this.getRemovedVendorTypes(snapshot, next)
      this.vendorParams.vendorType = next
      if (!removedTypes.length) {
        return
      }
      const nextAfterPrune = this.getPrunedRecipientsByVendorSelection({
        selectedAfterRemove: next,
        removedTypes,
      })
      const remainingSelectableTrees = this.getPrunedRecipientsByVendorSelection({
        selectedAfterRemove: next,
        removedTypes,
        recipientTrees: this.searchRecipientsResult,
      })
      this.confirmAndApplyPruneIfNeeded({
        nextAfterPrune,
        remainingSelectableTrees,
        onCancel: () => {
          this.vendorParams.vendorType = snapshot
        },
      })
    },
    async _searchRecipients () {
      if (this.isVendorTypeEmpty) return
      const departmentId = get(this.currentECircular, 'category.department.id')
      const request = {
        eCircularType: this.currentECircular.type,
        companyName: this.vendorParams.companyName.trim(),
        department: departmentId,
        vendorType: this.vendorParams.vendorType,
        companyId: this.vendorParams.companyId,
      }
      this.syncVendorSearchConditionsToStore()
      await this.searchRecipients(request)
      this.initSelectRecipientsTree('select-recipients')
    },
  },
}
</script>

<style scoped lang='scss'>
label {
  font-weight: bold;
  color: #043251;
  height: 25px;
  margin-top: 0;
}
.company-name-search-attribute {
  display: flex;
  justify-content: space-between;
}
</style>
